import { useSettingsStore } from '@/stores/settings';

/**
 * Get the active number format locale
 */
function getNumberLocale(): string {
  const settingsStore = useSettingsStore();
  return settingsStore.activeNumberFormat;
}

/**
 * Format a number with thousand separators
 * @param value - The number to format
 * @param decimals - Number of decimal places (optional)
 * @returns Formatted number string
 */
export function formatNumber(value: number | string, decimals?: number): string {
  const number_ = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(number_)) {
    return '0';
  }

  return new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number_);
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: USD)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number | string, currency: string = 'USD', decimals: number = 2): string {
  const number_ = typeof amount === 'string' ? Number.parseFloat(amount) : amount;

  if (Number.isNaN(number_)) {
    return `0 ${currency}`;
  }

  try {
    return new Intl.NumberFormat(getNumberLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number_);
  } catch {
    return `${formatAmount(number_, decimals)} ${currency}`;
  }
}

/**
 * Format a number as a percentage
 * @param value - The value to format (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number | string, decimals: number = 1): string {
  const number_ = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(number_)) {
    return '0%';
  }

  return `${new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number_)}%`;
}

/**
 * Format a decimal amount (like balance or transaction amount)
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted amount string
 */
export function formatAmount(amount: number | string, decimals: number = 2): string {
  const number_ = typeof amount === 'string' ? Number.parseFloat(amount) : amount;

  if (Number.isNaN(number_)) {
    return '0.00';
  }

  return new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number_);
}

/**
 * Format amount with currency symbol
 * @param amount - The amount to format
 * @param currency - Currency code
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "123.45 EUR"
 */
export function formatAmountWithCurrency(amount: number | string, currency: string, decimals: number = 2): string {
  return `${formatAmount(amount, decimals)} ${currency}`;
}

/**
 * Format a number in compact notation, for chart axes and other tight spaces
 * @param value - The value to format
 * @returns Formatted string like "1.2M"
 */
export function formatCompactNumber(value: number | string): string {
  const number_ = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(number_)) {
    return '0';
  }

  return new Intl.NumberFormat(getNumberLocale(), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number_);
}
